import {
  Box,
  Flex,
  HStack,
  Icon,
  IconButton,
  Text,
  VStack,
} from "@chakra-ui/react";
import { get } from "lodash";
import { useMemo } from "react";
import {
  BiSolidFileBlank,
  BiSolidFileImage,
  BiSolidFilePdf,
} from "react-icons/bi";
import { FiX } from "react-icons/fi";
import { FileInputImage } from ".";
import Image from "../Image";

export type FileType = "image" | "pdf";

interface FileViewProps {
  imageFiles: FileInputImage[];
  isFluid?: boolean;
  onRemove: (file: FileInputImage) => void;
}

export const FILE_TYPE_ICON_MAP = Object.freeze({
  image: BiSolidFileImage,
  pdf: BiSolidFilePdf,
  default: BiSolidFileBlank,
});

export function FileThumbnailView({ imageFiles, onRemove }: FileViewProps) {
  return (
    <HStack gap="1rem">
      {imageFiles?.map((imgFile, index) => (
        <Box
          borderRadius="5px"
          overflow="hidden"
          display="inline-block"
          position="relative"
          key={`file_thumbnail_preview_${index}`}
        >
          <Image
            sx={{
              height: "10rem",
              width: "10rem",
              objectFit: "cover",
              position: "relative",
              zIndex: 1,
            }}
            src={imgFile.preview}
            alt={imgFile.title}
          />

          <IconButton
            size="xs"
            position="absolute"
            colorScheme="blackAlpha"
            zIndex="popover"
            sx={{
              top: 2,
              right: 2,
              backgroundColor: "blackAlpha.800",
            }}
            rounded="full"
            onClick={() => {
              onRemove(imgFile);
            }}
            aria-label="remove-image"
            icon={<Icon as={FiX} />}
          />
        </Box>
      ))}
    </HStack>
  );
}

export function FileListView({ imageFiles, onRemove }: FileViewProps) {
  return (
    <VStack gap="1rem">
      {imageFiles?.map((imgFile, index) => (
        <HStack
          w="full"
          border="1px"
          borderColor="surface.300"
          px="1rem"
          color="surface.600"
          borderRadius="10px"
          alignItems="center"
          py="0.5rem"
          key={`file_preview_${index}`}
        >
          <Flex alignItems="center" flex={1}>
            <Icon
              fontSize="4xl"
              as={get(
                FILE_TYPE_ICON_MAP,
                imgFile?.file?.type ?? "",
                FILE_TYPE_ICON_MAP.default,
              )}
              aria-label="file_type"
            />
            <Box ml="0.5rem" flex={1}>
              <Text
                size="md"
                color="surface.700"
                fontWeight="semibold"
                textTransform="capitalize"
              >
                {imgFile.title}
              </Text>
              <Text size="xs">{imgFile?.file?.size}</Text>
            </Box>
          </Flex>
          <Box>
            <IconButton
              size="md"
              colorScheme="surface"
              zIndex="popover"
              variant="ghost"
              rounded="full"
              onClick={() => {
                onRemove(imgFile);
              }}
              aria-label="remove-image"
              icon={<Icon as={FiX} />}
            />
          </Box>
        </HStack>
      ))}
    </VStack>
  );
}

export function FileSingeView({
  imageFiles,
  onRemove,
  isFluid,
}: FileViewProps) {
  const imageFile = useMemo(() => imageFiles[0], [imageFiles]);
  return imageFile ? (
    <Box
      w={isFluid ? "full" : "10rem"}
      h={isFluid ? "20rem" : "10rem"}
      background="surface.100"
      borderRadius="10px"
      overflow="hidden"
      display="inline-block"
      position="relative"
    >
      <Image
        w={isFluid ? "full" : "10rem"}
        h={isFluid ? "20rem" : "10rem"}
        src={imageFile.preview}
        objectFit="contain"
        alt={imageFile.title}
      />

      <IconButton
        size="xs"
        position="absolute"
        colorScheme="blackAlpha"
        zIndex="popover"
        sx={{
          top: 2,
          right: 2,
          backgroundColor: "blackAlpha.800",
        }}
        rounded="full"
        onClick={() => {
          onRemove(imageFile);
        }}
        aria-label="remove-image"
        icon={<Icon as={FiX} />}
      />
    </Box>
  ) : null;
}
